import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

export const REPORT_GROUPS = [
  { key: "overzicht", label: "Overzicht" },
  { key: "detail", label: "Detail" },
  { key: "risico", label: "Risico" },
];

const STORAGE_KEY = "clubfin:report-sections:v1";
const readStored = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; } };

// Houdt per sectie bij of hij open staat; de stand wordt onthouden tussen bezoeken.
export function useReportSections(sections) {
  const [openMap, setOpenMap] = useState(() => {
    const stored = readStored();
    return Object.fromEntries(sections.map((s) => [s.id, stored[s.id] ?? s.defaultOpen !== false]));
  });
  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(openMap)); } catch { /* ignore */ } }, [openMap]);
  const toggle = useCallback((id, value) => setOpenMap((m) => ({ ...m, [id]: value === undefined ? !m[id] : value })), []);
  const setAll = useCallback((value) => setOpenMap((m) => Object.fromEntries(Object.keys(m).map((k) => [k, value]))), []);
  return { openMap, toggle, setAll };
}

// Inhoudsbalk met chips per sectie, gegroepeerd in Overzicht · Detail · Risico. Klik = uitklappen + scrollen.
export function ReportNav({ sections, openMap, onJump, onAll }) {
  const visible = sections.filter((s) => !s.hidden);
  return (
    <nav data-testid="report-nav" className="sticky top-[74px] z-30 -mx-6 px-6 py-2 mb-6 bg-background/95 backdrop-blur border-b-2 border-foreground/80 flex items-center gap-x-5 gap-y-2 flex-wrap print:hidden">
      {REPORT_GROUPS.map((g) => {
        const items = visible.filter((s) => s.group === g.key);
        if (!items.length) return null;
        return (
          <div key={g.key} className="flex items-center gap-1.5 flex-wrap">
            <span className="stat-label mr-1">{g.label}</span>
            {items.map((s) => (
              <button key={s.id} type="button" onClick={() => onJump(s.id)} data-testid={`report-nav-${s.id}`}
                className={`px-2 py-1 border-2 text-[10px] font-display uppercase font-bold tracking-wide transition-colors ${openMap[s.id] ? "border-foreground bg-card hover:bg-primary hover:text-primary-foreground" : "border-dashed border-muted-foreground text-muted-foreground hover:border-foreground hover:text-foreground"}`}>
                {s.short || s.title}
              </button>
            ))}
          </div>
        );
      })}
      <div className="ml-auto flex items-center gap-2 text-[10px] font-display uppercase font-bold tracking-widest text-muted-foreground">
        <button type="button" onClick={() => onAll(true)} data-testid="report-expand-all" className="hover:text-foreground underline underline-offset-2">Alles uitklappen</button>
        <span>·</span>
        <button type="button" onClick={() => onAll(false)} data-testid="report-collapse-all" className="hover:text-foreground underline underline-offset-2">Alles inklappen</button>
      </div>
    </nav>
  );
}

// Dunne sectiebalk boven elk rapportonderdeel; ingeklapt = alleen de balk.
export function ReportSection({ id, group, title, open, forceOpen = false, onToggle, hidden = false, children }) {
  if (hidden) return null;
  const isOpen = open || forceOpen;
  const groupLabel = REPORT_GROUPS.find((g) => g.key === group)?.label || "";
  return (
    <section id={`report-${id}`} data-testid={`report-section-${id}`} data-open={isOpen ? "1" : "0"} className="scroll-mt-[128px] mb-8">
      <button type="button" onClick={() => onToggle(id)} data-testid={`report-toggle-${id}`} aria-expanded={isOpen}
        className={`w-full flex items-center justify-between gap-3 px-3 py-2 mb-2 border-2 text-left transition-colors ${isOpen ? "border-foreground/30 bg-transparent hover:bg-muted/40" : "border-foreground bg-card heavy-shadow hover:bg-muted/40"}`}>
        <span className="min-w-0 flex items-baseline gap-2 flex-wrap">
          <span className="stat-label">{groupLabel}</span>
          <span className="font-display uppercase font-black text-sm tracking-tight">{title}</span>
        </span>
        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-display uppercase font-bold tracking-widest text-muted-foreground">
          {isOpen ? <>Inklappen <ChevronUp className="w-3.5 h-3.5" /></> : <>Uitklappen <ChevronDown className="w-3.5 h-3.5" /></>}
        </span>
      </button>
      {isOpen && children}
    </section>
  );
}
